/**
 * Static catalog for the Growth Stages feature matrix.
 *
 * The offering catalog is not modeled in the database; it is a content
 * snapshot owned by product and edited via PR. Column marketing copy is
 * keyed by stage position (1-4) so the "YOU ARE HERE" marker can match on
 * the member company's `companyStage.position` regardless of how the DB
 * stage catalog is named.
 *
 * Source of truth: docs/features/stage-management/06-member-stage-feature-matrix/spec.md
 * (Content Inventory).
 */

export type StagePosition = 1 | 2 | 3 | 4;

export type CellState = "not-included" | "new" | "carried";

export interface StageColumn {
  position: StagePosition;
  label: string;
  /** Per-stage accent hue (hex), applied on top of theme-following surfaces. */
  accent: string;
}

export interface Offering {
  name: string;
  description: string;
  /** Kept in the data but no longer rendered (ADO 9570); pills may return. */
  tag: string;
  /** First stage that includes the offering; null for a la carte-only items. */
  entryStagePosition: StagePosition | null;
  alaCarte: boolean;
}

export interface OfferingCategory {
  name: string;
  tagline: string;
  /** Category accent hue (hex) for the header tint bar and swatch. */
  accent: string;
  offerings: Offering[];
}

export const stageColumns: StageColumn[] = [
  { position: 1, label: "Build", accent: "#34D399" },
  { position: 2, label: "Grow", accent: "#60A5FA" },
  { position: 3, label: "Lead", accent: "#A78BFA" },
  { position: 4, label: "Scale", accent: "#FBBF24" },
];

export const offeringCatalog: OfferingCategory[] = [
  {
    name: "Coaching",
    tagline:
      "1-on-1 and group coaching from CertainPath's focused, passionate experts.",
    accent: "#60A5FA",
    offerings: [
      {
        name: "Onboarding Coaching",
        description:
          "A CertainPath onboarding coach sets your foundations from Day One.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Coach Connect (Field Focus)",
        description:
          "Group coaching calls on topics related to your field operations.",
        tag: "INSPECT",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "The Accountability Group (TAG) - Stage-Based Groups",
        description:
          "Monthly calls with your stage's Accountability Group to stay on track.",
        tag: "INSPECT",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Specialty Coaching",
        description:
          "Access to CertainPath experts on marketing, financials, and more.",
        tag: "FINANCIALS",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Coach Connect (Management Focus)",
        description:
          "Group coaching calls on trending managerial focused topics.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: false,
      },
      {
        name: "Strategic Operation Action Review (SOAR) Program",
        description:
          "Multi-day on-site strategic review plus follow-up coaching for results.",
        tag: "INSPECT",
        entryStagePosition: null,
        alaCarte: true,
      },
    ],
  },
  {
    name: "Community",
    tagline: "Events, podcasts, and gatherings where you meet your peers.",
    accent: "#34D399",
    offerings: [
      {
        name: "CertainPath Expo Attendance (Spring)",
        description:
          "The industry's best event: gain insights and network with 1,000+ members.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "CertainPath Awards Gala Team Table",
        description:
          "A reserved premium team table at the annual CertainPath Awards Gala.",
        tag: "INSPECT",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "CertainPath Connect Attendance (Fall)",
        description:
          "Connect and network with fellow members in unbeatable locations.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "CertainPath Eagles Summit Attendance",
        description:
          "Our annual event for our largest members to network and learn together.",
        tag: "INSPECT",
        entryStagePosition: 4,
        alaCarte: false,
      },
    ],
  },
  {
    name: "Dashboard",
    tagline: "The numbers your operating system needs to surface, and how.",
    accent: "#FB923C",
    offerings: [
      {
        name: "Insights Report: Total Sales - New vs Existing Customers",
        description:
          "Focus marketing by revenue split between new and existing customers.",
        tag: "REPORTS",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Insights Report: Total Sales by Year & Month",
        description:
          "Spot busy times and shoulder months with a time-series revenue view.",
        tag: "REPORTS",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Insights Report: Revenue by Invoice Age",
        description:
          "Make financial decisions from an aging view of outstanding revenue.",
        tag: "REPORTS",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Insights Report: Average Invoice Comparison",
        description:
          "See how your average invoice has trended over different periods.",
        tag: "REPORTS",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Insights Report: Total Sales by ZIP Code",
        description:
          "See which zip codes drive results with total sales by zip code.",
        tag: "MARKETING",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Insights Report: Total Sales from New Customers by ZIP & Year",
        description:
          "See where new customer sales come from to guide service and marketing.",
        tag: "MARKETING",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Insights Report: Percentage of New Customers by ZIP",
        description:
          "See where your new customers come from to focus your marketing.",
        tag: "MARKETING",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Insights Report: Total Households by ZIP",
        description:
          "Spot the true potential in your market and respond accordingly.",
        tag: "MARKETING",
        entryStagePosition: 2,
        alaCarte: false,
      },
      {
        name: "Insights Report: New Customer Replacement Efficiency",
        description:
          "See how often new-customer calls turn into replacement sales.",
        tag: "REPORTS",
        entryStagePosition: 3,
        alaCarte: false,
      },
      {
        name: "Insights Report: Existing Customer Replacement Efficiency",
        description:
          "See how often existing-customer calls turn into replacement sales.",
        tag: "REPORTS",
        entryStagePosition: 3,
        alaCarte: false,
      },
      {
        name: "Insights Report: Lifetime Value by Tier",
        description:
          "See the long-term lifetime value of each tier of your customers.",
        tag: "MARKETING",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "Insights Report: Total Addressable Market",
        description:
          "See your business potential through the addressable market near you.",
        tag: "MARKETING",
        entryStagePosition: 4,
        alaCarte: false,
      },
    ],
  },
  {
    name: "Group Buying",
    tagline: "Negotiated pricing and rebates across the partner network.",
    accent: "#F472B6",
    offerings: [
      {
        name: "CertainPath Partner Network",
        description:
          "Tap into CertainPath's group buying power to earn industry-leading rebates.",
        tag: "INVENTORY",
        entryStagePosition: 1,
        alaCarte: false,
      },
    ],
  },
  {
    name: "Marketing",
    tagline: "Done-for-you demand generation to keep your pipeline full.",
    accent: "#F87171",
    offerings: [
      {
        name: "CertainPath Lead Generation",
        description:
          "CertainPath's marketing service with campaigns that turn data into measurable revenue.",
        tag: "MARKETING",
        entryStagePosition: null,
        alaCarte: true,
      },
    ],
  },
  {
    name: "Software",
    tagline: "The CertainPath operating platform.",
    accent: "#22D3EE",
    offerings: [
      {
        name: "CertainPath Software",
        description:
          "Field management software to run a profitable operation built on the CertainPath system.",
        tag: "DISPATCH",
        entryStagePosition: null,
        alaCarte: true,
      },
    ],
  },
  {
    name: "Trademark",
    tagline:
      "Brand seals that signal to homeowners you've earned the right to be in their home.",
    accent: "#FBBF24",
    offerings: [
      {
        name: "CertainPath Unique Selling Propositions (Trademarks)",
        description:
          "Use members-only trademarks to build trust and stand out locally.",
        tag: "MARKETING",
        entryStagePosition: 1,
        alaCarte: false,
      },
    ],
  },
  {
    name: "Training",
    tagline:
      "Classroom, virtual, and onsite training that turns knowledge into shop-floor change.",
    accent: "#A78BFA",
    offerings: [
      {
        name: "Executive Perspective",
        description:
          "The most powerful training week; return as many times as you want.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "HUB Resource Library - Stage Related Resources",
        description:
          "The CertainPath system of tools, resources, and courses curated for your stage.",
        tag: "INSPECT",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Service Essentials Course",
        description:
          "Our signature course in the CertainPath service system for technicians.",
        tag: "RUN CALL",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Telephone Essentials Course",
        description:
          "Master how your team answers and books more calls than ever.",
        tag: "CALL INTAKE",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Dispatching for Profits Course",
        description:
          "Dispatching for Profits puts the right tech on every call.",
        tag: "DISPATCH",
        entryStagePosition: 1,
        alaCarte: false,
      },
      {
        name: "Electricians Advanced Course",
        description:
          "Take electricians beyond the basics to master selling in the home.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "HVAC Sales Course",
        description:
          "CertainPath Sales System training for advisors and selling techs.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Plumbers Advanced Course",
        description:
          "Take plumbing techs beyond the basics to master selling in the home.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Roofing Sales Course",
        description:
          "CertainPath Sales System training for your roofing sales specialists.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Fundamentals of Drain Cleaning",
        description:
          "Hands-on drain cleaning class covering technical and sales skills.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Advanced Selling Course",
        description:
          "Dive into the psychology of sales techniques your team can use today.",
        tag: "RUN CALL",
        entryStagePosition: 2,
        alaCarte: true,
      },
      {
        name: "Foundational Leadership Course",
        description:
          "Discover the foundation of becoming a more effective leader.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Emotional Intelligence Course",
        description:
          "Build a strong EQ for leading, communicating, and selling in the home.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Developing Effective Managers Course",
        description:
          "Give your management team the foundation to be successful managers.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Planning for Success Course",
        description:
          "An in-depth annual course to help you budget and plan the year ahead.",
        tag: "DISPATCH",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Recruit and Retain Top Talent Course",
        description:
          "Learn to hire the right people and build a culture that retains them.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Sales Management Course",
        description:
          "Three-day course to grow, manage, and coach your sales team.",
        tag: "RUN CALL",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "Service Management Course",
        description:
          "Three-day course with all a service manager needs to lead a team.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: true,
      },
      {
        name: "The Lion's Pride (Group-Based)",
        description:
          "Build your leadership skills in small group training with your fellow members.",
        tag: "INSPECT",
        entryStagePosition: 3,
        alaCarte: false,
      },
      {
        name: "Call Center Mastery Group",
        description:
          "Weekly coaching and accountability calls for call center managers.",
        tag: "CALL INTAKE",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "Sales Management Mastery Group",
        description:
          "Weekly training and accountability calls for you and your sales manager.",
        tag: "RUN CALL",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "The Lion's Pride (Individual-Based)",
        description:
          "Customize your leadership journey to match the needs of your business and life.",
        tag: "INSPECT",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "RevenueAscent",
        description:
          "Ongoing reinforcement training to make results stick along with management review calls.",
        tag: "RUN CALL",
        entryStagePosition: 4,
        alaCarte: false,
      },
      {
        name: "Developing Winning Trainers Course",
        description:
          "Give your managers the skills to become winning trainers in-house.",
        tag: "INSPECT",
        entryStagePosition: 4,
        alaCarte: true,
      },
      {
        name: "CertainPath FieldCoach",
        description:
          "Virtual ride-alongs to coach your team from what really happens on every call.",
        tag: "RUN CALL",
        entryStagePosition: null,
        alaCarte: true,
      },
    ],
  },
];

/**
 * Resolves how an offering should render in a given stage column:
 * "not-included" before its entry stage (or for a la carte-only items),
 * "new" at its entry stage, "carried" for every stage after entry.
 */
export function cellState(
  offering: Offering,
  stagePosition: StagePosition,
): CellState {
  const entry = offering.entryStagePosition;
  if (entry === null || stagePosition < entry) {
    return "not-included";
  }
  if (stagePosition === entry) {
    return "new";
  }
  return "carried";
}
